/**
 * Electron Notification Listener (Online Mode Only)
 *
 * Subscribes to Firestore `tenants/{tenantId}/incomingEvents` via onSnapshot
 * so the Electron renderer receives real-time notifications without FCM.
 *
 * - Only activates when running inside Electron (`window.electronAPI`).
 * - Handles only `added` document changes (ignores modified/removed).
 * - Routes new events through the existing emitAppNotification pipeline.
 * - Returns an unsubscribe function for cleanup on sign-out / teardown.
 */

import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { emitAppNotification } from './appNotifications';

let activeUnsub = null;

// Backend sends severity as normal/high/emergency; client expects info/warning/critical
const SEVERITY_MAP = { normal: 'info', high: 'warning', emergency: 'critical' };
function normalizeSeverity(raw) {
  if (!raw) return 'info';
  const lower = String(raw).toLowerCase();
  return SEVERITY_MAP[lower] || lower;
}

function shouldEmitAppNotification(data) {
  const source = String(data?.source || '').toLowerCase();
  const type = String(data?.type || '').toLowerCase();
  const isNotificationType = type === 'notification' || type === 'notification.response';

  // Electron online delivery is intended to mirror push notification delivery,
  // not every canonical incomingEvent. Paging/session/internal-trigger lifecycle
  // events are logged as system status records and should not surface as user
  // notifications in the desktop shell.
  if (!isNotificationType) {
    return false;
  }

  // The canonical push path uses source=push and type=notification.
  // Narrow to that path so operational lifecycle/status events logged in
  // incomingEvents do not render as user notifications in Electron.
  return source === 'push' && type === 'notification';
}

function hasResponseOptions(rawResponses) {
  if (!rawResponses) return false;
  if (Array.isArray(rawResponses)) return rawResponses.length > 0;
  if (typeof rawResponses === 'string') {
    try {
      const parsed = JSON.parse(rawResponses);
      return Array.isArray(parsed) && parsed.length > 0;
    } catch (_) {
      return false;
    }
  }
  return false;
}

function hasUserFacingContent(data) {
  const meta = data?.context?.meta || {};
  const title = String(meta.title || '').trim();
  const message = String(meta.message || '').trim();
  const actionId = String(meta.actionId || data?.actionId || '').trim();
  const responses = meta.responses || data?.responses || null;
  return Boolean(title || message || actionId || hasResponseOptions(responses));
}

/**
 * Start listening for new incomingEvents for the current user's tenant.
 *
 * @param {Object} opts
 * @param {string} opts.tenantId  - Current tenant ID
 * @param {string} opts.userId    - Current user ID (used to skip self-notifications)
 * @param {number} [opts.docLimit=25] - Max docs in the snapshot query
 * @returns {Function} unsubscribe – call to tear down the listener
 */
export function startElectronNotificationListener({ tenantId, userId, docLimit = 25 }) {
  // Only run inside Electron
  if (typeof window === 'undefined' || !window.electronAPI) {
    try { console.log('[ELECTRON-NOTIF] Not in Electron, skipping listener'); } catch (_) {}
    return () => {};
  }

  if (!tenantId) {
    try { console.warn('[ELECTRON-NOTIF] No tenantId, cannot start listener'); } catch (_) {}
    return () => {};
  }

  // Tear down any previous listener before starting a new one
  stopElectronNotificationListener();

  try {
    console.log(
      '[ELECTRON-NOTIF] Starting onSnapshot listener ' +
        JSON.stringify({ tenantId, userId, docLimit })
    );
  } catch (_) {}

  // Track whether this is the initial snapshot (we skip it to avoid replaying old events)
  let initialSnapshotReceived = false;

  const colRef = collection(db, 'tenants', tenantId, 'incomingEvents');
  const q = query(
    colRef,
    orderBy('timestamp', 'desc'),
    limit(docLimit)
  );

  const unsub = onSnapshot(
    q,
    (snapshot) => {
      try {
        // Skip the very first snapshot — it contains existing docs, not new ones
        if (!initialSnapshotReceived) {
          initialSnapshotReceived = true;
          try {
            console.log(
              '[ELECTRON-NOTIF] Initial snapshot received, skipping ' +
                snapshot.docChanges().length + ' existing docs'
            );
          } catch (_) {}
          return;
        }

        const added = snapshot.docChanges().filter((c) => c.type === 'added');
        if (added.length === 0) return;

        try {
          console.log('[ELECTRON-NOTIF] ' + added.length + ' new doc(s) received');
        } catch (_) {}

        for (const change of added) {
          const data = change.doc.data();

          // Push notifications must reach Home even when this user ran Test Flow.
          const isPushNotification = String(data?.source || '').toLowerCase() === 'push'
            && String(data?.type || '').toLowerCase() === 'notification';
          const actorUserId = data?.actor?.userId;
          if (!isPushNotification && userId && actorUserId && actorUserId === userId) {
            try {
              console.log('[ELECTRON-NOTIF] Skipping self-notification from ' + actorUserId);
            } catch (_) {}
            continue;
          }

          // Map Firestore doc to canonical event for emitAppNotification
          const meta = data.context?.meta || {};
          if (!shouldEmitAppNotification(data)) {
            try {
              console.log(
                '[ELECTRON-NOTIF] Skipping non-user-facing event ' +
                  JSON.stringify({
                    eventId: data.eventId || change.doc.id,
                    source: data.source || 'unknown',
                    type: data.type || 'unknown',
                    route: data.context?.route || null,
                    status: data.context?.status || null,
                  })
              );
            } catch (_) {}
            continue;
          }

          if (!hasUserFacingContent(data)) {
            try {
              console.log(
                '[ELECTRON-NOTIF] Skipping empty/non-actionable notification payload ' +
                  JSON.stringify({
                    eventId: data.eventId || change.doc.id,
                    source: data.source || 'unknown',
                    type: data.type || 'unknown',
                  })
              );
            } catch (_) {}
            continue;
          }

          const event = {
            id: data.eventId || change.doc.id,
            severity: normalizeSeverity(data.severity),
            visibility: meta.visibility || data.visibility || 'in_app_only',
            source: data.source || 'system',
            type: data.type || 'notification',
            timestamp: data.timestamp || new Date().toISOString(),
            context: data.context || {},
            actor: data.actor || {},
            alertMeta: data.alertMeta || null,
            actionId: meta.actionId || data.actionId || null,
            responses: meta.responses || data.responses || null,
          };

          try {
            emitAppNotification(event, { skipOSNotification: false });
          } catch (e) {
            try {
              console.warn('[ELECTRON-NOTIF] emitAppNotification failed:', e?.message);
            } catch (_) {}
          }
        }
      } catch (err) {
        try {
          console.error('[ELECTRON-NOTIF] Snapshot handler error:', err?.message);
        } catch (_) {}
      }
    },
    (error) => {
      try {
        console.error(
          '[ELECTRON-NOTIF] onSnapshot error: ' +
            JSON.stringify({ code: error?.code, message: error?.message })
        );
      } catch (_) {}
    }
  );

  activeUnsub = unsub;

  try {
    console.log('[ELECTRON-NOTIF] Listener active');
  } catch (_) {}

  return unsub;
}

/**
 * Stop the active Electron notification listener if one exists.
 */
export function stopElectronNotificationListener() {
  if (activeUnsub) {
    try {
      activeUnsub();
      console.log('[ELECTRON-NOTIF] Listener stopped');
    } catch (_) {}
    activeUnsub = null;
  }
}
